import React, { useState, useEffect, useRef, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  X,
  Search,
  Image as ImageIcon,
  Film,
  Check,
  Loader2,
  FolderOpen,
} from "lucide-react";
import { mediaAPI } from "../../services/api";

interface MediaItem {
  id: string;
  filename: string;
  original_url: string;
  thumbnail_url?: string;
  media_type: string;
  mime_type: string;
  file_size: number;
  width?: number;
  height?: number;
  duration?: number;
  created_at: string;
}

interface MediaPickerModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (item: MediaItem) => void;
  /** Filter to show only these media types. Defaults to image+video. */
  allowedTypes?: string[];
}

const PAGE_SIZE = 24;

export default function MediaPickerModal({
  open,
  onClose,
  onSelect,
  allowedTypes = ["image", "video", "gif"],
}: MediaPickerModalProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<MediaItem | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Reset state when opened
  useEffect(() => {
    if (open) {
      setSearch("");
      setDebouncedSearch("");
      setTypeFilter("all");
      setPage(1);
      setSelected(null);
      setTimeout(() => searchRef.current?.focus(), 100);
    }
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) { return; }
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") { onClose(); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  const mediaTypeParam = typeFilter === "all" ? undefined : typeFilter;

  const { data, isLoading } = useQuery({
    queryKey: ["media-picker", debouncedSearch, mediaTypeParam, page],
    queryFn: async () => {
      const resp = await mediaAPI.list({
        search: debouncedSearch || undefined,
        media_type: mediaTypeParam,
        page,
        limit: PAGE_SIZE,
      });
      return resp.data;
    },
    enabled: open,
    staleTime: 30_000,
  });

  const items: MediaItem[] = (data?.items || data?.media || []).filter(
    (m: MediaItem) => allowedTypes.includes(m.media_type)
  );
  const totalPages = data?.total_pages ?? 1;

  const handleSelect = useCallback(() => {
    if (selected) {
      onSelect(selected);
      onClose();
    }
  }, [selected, onSelect, onClose]);

  // Generate video thumbnail from first frame
  const VideoThumb = ({ src }: { src: string }) => {
    const ref = useRef<HTMLVideoElement>(null);
    const [thumb, setThumb] = useState<string | null>(null);

    useEffect(() => {
      const video = ref.current;
      if (!video) { return; }
      const handleLoaded = () => {
        video.currentTime = 0.1;
      };
      const handleSeeked = () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          const ctx = canvas.getContext("2d");
          if (ctx) {
            ctx.drawImage(video, 0, 0);
            setThumb(canvas.toDataURL("image/jpeg", 0.6));
          }
        } catch {
          // cross-origin, ignore
        }
      };
      video.addEventListener("loadeddata", handleLoaded);
      video.addEventListener("seeked", handleSeeked);
      return () => {
        video.removeEventListener("loadeddata", handleLoaded);
        video.removeEventListener("seeked", handleSeeked);
      };
    }, [src]);

    return (
      <>
        <video ref={ref} src={src} crossOrigin="anonymous" muted preload="metadata" className="hidden" />
        {thumb ? (
          <img src={thumb} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-100">
            <Film size={28} className="text-gray-300" />
          </div>
        )}
      </>
    );
  };

  if (!open) { return null; }

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div
      ref={backdropRef}
      role="dialog"
      aria-modal="true"
      aria-label={t("editor_pick_from_library", "Media Library")}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={(e) => { if (e.target === backdropRef.current) { onClose(); } }}
      onKeyDown={(e) => { if (e.key === "Escape") { onClose(); } }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-[780px] max-w-[95vw] max-h-[85vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
              <FolderOpen size={16} className="text-blue-600" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-900">
                {t("editor_pick_from_library", "Media Library")}
              </h2>
              <p className="text-xs text-gray-400">
                {t("editor_pick_subtitle", "Select an image or video to edit")}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition"
          >
            <X size={18} />
          </button>
        </div>

        {/* Filters bar */}
        <div className="px-5 py-3 flex items-center gap-3 border-b border-gray-50">
          {/* Search */}
          <div className="relative flex-1 max-w-xs">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder={t("editor_search_media", "Search files...")}
              className="w-full pl-8 pr-3 py-2 text-xs bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-400 focus:bg-white transition"
            />
          </div>

          {/* Type chips */}
          <div className="flex gap-1.5">
            {[
              { key: "all", label: t("editor_filter_all", "All") },
              { key: "image", label: t("editor_filter_images", "Images"), icon: ImageIcon },
              { key: "video", label: t("editor_filter_videos", "Videos"), icon: Film },
            ]
              .filter((f) => f.key === "all" || allowedTypes.includes(f.key))
              .map((f) => (
                <button
                  key={f.key}
                  onClick={() => { setTypeFilter(f.key); setPage(1); }}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg border transition"
                  style={{
                    borderColor: typeFilter === f.key ? "#3b82f6" : "#e5e7eb",
                    backgroundColor: typeFilter === f.key ? "#eff6ff" : "white",
                    color: typeFilter === f.key ? "#3b82f6" : "#6b7280",
                  }}
                >
                  {f.icon && <f.icon size={12} />}
                  {f.label}
                </button>
              ))}
          </div>
        </div>

        {/* Grid */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {isLoading ? (
            <div className="flex items-center justify-center h-48">
              <Loader2 size={28} className="animate-spin text-blue-500" />
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-gray-400">
              <FolderOpen size={36} className="mb-2 text-gray-300" />
              <p className="text-sm">{t("editor_no_media", "No media found")}</p>
              <p className="text-xs mt-1">{t("editor_no_media_hint", "Try a different search or upload files from the Media Library page")}</p>
            </div>
          ) : (
            <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-2.5">
              {items.map((item) => {
                const isSelected = selected?.id === item.id;
                const isImage = item.media_type === "image" || item.media_type === "gif";

                return (
                  <button
                    key={item.id}
                    onClick={() => setSelected(isSelected ? null : item)}
                    onDoubleClick={() => { setSelected(item); onSelect(item); onClose(); }}
                    className="group relative aspect-square rounded-xl overflow-hidden border-2 transition-all focus:outline-none focus:ring-2 focus:ring-blue-400"
                    style={{
                      borderColor: isSelected ? "#3b82f6" : "transparent",
                    }}
                    title={item.filename}
                  >
                    {isImage ? (
                      <img
                        src={item.thumbnail_url || item.original_url}
                        alt={item.filename}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <VideoThumb src={item.original_url} />
                    )}

                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition" />

                    {/* Selected check */}
                    {isSelected && (
                      <div className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center shadow">
                        <Check size={14} className="text-white" />
                      </div>
                    )}

                    {/* Video badge */}
                    {!isImage && (
                      <div className="absolute bottom-1.5 left-1.5 px-1.5 py-0.5 rounded bg-black/60 text-[10px] text-white flex items-center gap-1">
                        <Film size={10} />
                        {item.duration ? `${Math.floor(item.duration / 60)}:${String(Math.floor(item.duration % 60)).padStart(2, "0")}` : "Video"}
                      </div>
                    )}

                    {/* Filename on hover */}
                    <div className="absolute bottom-0 left-0 right-0 px-1.5 py-1 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition">
                      <p className="text-[10px] text-white truncate">{item.filename}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between">
          {/* Pagination */}
          <div className="flex items-center gap-2">
            {totalPages > 1 && (
              <>
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="px-2.5 py-1 text-xs rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-30 transition"
                >
                  {t("editor_prev", "Prev")}
                </button>
                <span className="text-xs text-gray-400">
                  {page} / {totalPages}
                </span>
                <button
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages}
                  className="px-2.5 py-1 text-xs rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-30 transition"
                >
                  {t("editor_next", "Next")}
                </button>
              </>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-xs text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition"
            >
              {t("editor_cancel", "Cancel")}
            </button>
            <button
              onClick={handleSelect}
              disabled={!selected}
              className="px-4 py-2 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition flex items-center gap-1.5"
            >
              <Check size={14} />
              {selected?.media_type === "video"
                ? t("editor_load_video", "Load Video")
                : t("editor_load_image", "Load Image")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
